<template>
  <Teleport to="body">
    <dialog
      ref="dialogEl"
      aria-label="Certificate viewer"
      aria-describedby="certificate-instructions"
      class="certificate-viewer fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none border-0 bg-[#f9f9fa] p-0 text-gray-800"
      @cancel.prevent="emit('close')"
      @keydown="handleKeydown"
    >
      <div class="flex h-full flex-col">
        <p id="certificate-instructions" class="sr-only">
          Scroll to read all pages. Use plus and minus to zoom, zero to fit, and
          Escape to close.
        </p>
        <header
          class="relative hidden h-[54px] shrink-0 items-center justify-between border-b border-[#e5e5e5] bg-white pl-[21px] pr-4 tablet-v:flex"
        >
          <div
            class="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <DacrIcon class="h-[30px] w-auto" aria-label="Dacr" />
          </div>
          <Button
            :icon="DownloadIcon"
            :disabled="downloading || !validUrl"
            variant="stroke"
            @click="download"
            >Download</Button
          >
          <Button
            :icon="CloseIcon"
            variant="ghost"
            aria-label="Close certificate"
            class="w-[30px] px-0!"
            @click="emit('close')"
          />
        </header>

        <div class="relative min-h-0 flex-1">
          <header
            ref="headerEl"
            class="absolute inset-x-0 top-0 z-10 h-[calc(env(safe-area-inset-top)+60px)] pt-[calc(env(safe-area-inset-top)+6px)] tablet-v:hidden"
          >
            <div
              class="absolute inset-0 bg-[#fbfbfb]/70 backdrop-blur-xl transition-opacity duration-250"
              :class="glassHeader ? 'opacity-100' : 'opacity-0'"
            />
            <div
              class="pointer-events-none absolute inset-x-0 bottom-2.5 top-[calc(env(safe-area-inset-top)+6px)] flex items-center justify-center"
            >
              <DacrIcon class="h-[30px] w-auto" aria-label="Dacr" />
            </div>
            <button
              type="button"
              aria-label="Close certificate"
              class="relative ml-4 flex size-11 cursor-pointer items-center justify-center rounded-full bg-[#fbfbfb]/70 backdrop-blur-xl"
              @click="emit('close')"
            >
              <span
                class="relative flex size-full items-center justify-center rounded-full"
              >
                <span aria-hidden="true" class="shadow--glass-pill"><i /></span>
                <CloseIcon class="size-6 text-gray-800" aria-hidden="true" />
              </span>
            </button>
          </header>

          <div
            ref="zoomViewportEl"
            class="absolute inset-0 overflow-hidden"
            :style="previewZoom.viewportStyle.value"
          >
            <div
              ref="paneEl"
              data-certificate-pane
              tabindex="0"
              autofocus
              aria-label="Certificate pages"
              class="absolute inset-0 origin-center overflow-y-auto overscroll-contain outline-none"
              :style="previewZoom.contentStyle.value"
              @scroll="updateGlassHeader"
              @pointerdown="startPan"
              @pointermove="pan"
              @pointerup="endPan"
              @pointercancel="endPan"
            >
              <div
                ref="pagesEl"
                class="flex min-h-full min-w-full w-max flex-col items-center justify-center gap-10 px-5 pb-5 pt-[calc(env(safe-area-inset-top)+80px)] tablet-v:p-[30px]"
                :class="zoom > 1 && 'cursor-grab active:cursor-grabbing'"
              >
                <template v-if="pdf && !failed">
                  <CertificatePage
                    v-for="pageNumber in pdf.numPages"
                    :key="`${attempt}-${pageNumber}`"
                    :document="pdf"
                    :page-number="pageNumber"
                    :available-width="pane.width"
                    :available-height="pane.height"
                    :zoom="1"
                    :render-scale="zoom"
                    @error="failed = true"
                    @rendered="pageNumber === 1 && (loading = false)"
                  />
                </template>
                <div
                  v-if="failed"
                  class="flex max-w-full flex-col items-center gap-4 px-4 text-center"
                  role="alert"
                >
                  <p>Could not load the certificate.</p>
                  <Button v-if="validUrl" variant="stroke" @click="load"
                    >Retry</Button
                  >
                  <a
                    v-if="validUrl"
                    :href="url"
                    target="_blank"
                    rel="noopener"
                    class="text-link"
                    >Open original PDF</a
                  >
                </div>
              </div>
              <div
                v-if="loading && !failed"
                role="status"
                class="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                <Spinner :size="24" /><span class="sr-only"
                  >Loading certificate</span
                >
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="downloadFailed"
          role="alert"
          class="shrink-0 bg-white px-4 py-2 text-center text-[14px]"
        >
          Could not download the certificate. Please try again.
        </p>
        <footer
          class="flex shrink-0 border-t border-[#e5e5e5] bg-white px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+20px)] tablet-v:hidden"
        >
          <Button
            :icon="DownloadIcon"
            :disabled="downloading || !validUrl"
            variant="stroke"
            size="lg"
            @click="download"
            >Download</Button
          >
        </footer>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue'
import { useRoute } from 'vue-router'
import { Button } from '@dacr/library'
import { CloseIcon, DacrIcon, DownloadIcon } from '@dacr/icons'
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist'
import Spinner from '@/shared/components/Spinner.vue'
import CertificatePage from './CertificatePage.vue'
import { certificateFilename } from '../certificateViewer'
import { usePreviewZoom } from '@/features/explorer/composables/usePreviewZoom'

const props = defineProps<{ url: string }>()
const emit = defineEmits<{ close: [] }>()
const route = useRoute()
const dialogEl = ref<HTMLDialogElement>()
const headerEl = ref<HTMLElement>()
const paneEl = ref<HTMLElement>()
const zoomViewportEl = ref<HTMLElement>()
const pagesEl = ref<HTMLElement>()
const pdf = shallowRef<PDFDocumentProxy>()
const pane = reactive({ width: 0, height: 0 })
const loading = ref(true)
const failed = ref(false)
const attempt = ref(0)
const downloading = ref(false)
const downloadFailed = ref(false)
const glassHeader = ref(false)
const previewZoom = usePreviewZoom(zoomViewportEl, paneEl, updateGlassHeader)
const zoom = previewZoom.scale
const validUrl = computed(() => {
  try {
    return ['https:', 'http:'].includes(new URL(props.url).protocol)
  } catch {
    return false
  }
})
let loadingTask: PDFDocumentLoadingTask | undefined
let observer: ResizeObserver | undefined
let revision = 0
let disposed = false
let drag: { x: number; y: number } | undefined
const downloadController = new AbortController()

async function load() {
  const current = ++revision
  const previous = loadingTask
  loadingTask = undefined
  pdf.value = undefined
  loading.value = true
  failed.value = false
  previewZoom.reset()
  attempt.value++
  await nextTick()
  await previous?.destroy().catch(() => {})
  if (disposed || current !== revision) return
  if (!validUrl.value) {
    failed.value = true
    return
  }
  try {
    const { loadCertificatePdf } = await import('../certificatePdf')
    if (disposed || current !== revision) return
    const task = loadCertificatePdf(props.url)
    loadingTask = task
    const loaded = await task.promise
    if (disposed || current !== revision) return
    pdf.value = loaded
    paneEl.value?.scrollTo(0, 0)
  } catch {
    if (!disposed && current === revision) failed.value = true
  }
}

function measure() {
  if (!paneEl.value || !pagesEl.value) return
  const style = getComputedStyle(pagesEl.value)
  pane.width = Math.max(
    1,
    paneEl.value.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight)
  )
  pane.height = Math.max(
    1,
    paneEl.value.clientHeight -
      parseFloat(style.paddingTop) -
      parseFloat(style.paddingBottom)
  )
  updateGlassHeader()
}

function updateGlassHeader() {
  const first = pagesEl.value?.querySelector('[data-certificate-page]')
  glassHeader.value = !!(
    headerEl.value?.offsetHeight &&
    first &&
    first.getBoundingClientRect().top <
      headerEl.value.getBoundingClientRect().bottom
  )
}

function startPan(event: PointerEvent) {
  if (
    event.pointerType !== 'mouse' ||
    event.button !== 0 ||
    zoom.value === 1 ||
    !(event.target instanceof HTMLCanvasElement) ||
    !paneEl.value
  )
    return
  drag = {
    x: event.clientX,
    y: event.clientY,
  }
  event.target.setPointerCapture(event.pointerId)
  event.preventDefault()
}

function pan(event: PointerEvent) {
  if (!drag) return
  previewZoom.panBy(event.clientX - drag.x, event.clientY - drag.y)
  drag = { x: event.clientX, y: event.clientY }
}

function endPan(event: PointerEvent) {
  drag = undefined
  if (
    event.target instanceof HTMLElement &&
    event.target.hasPointerCapture(event.pointerId)
  )
    event.target.releasePointerCapture(event.pointerId)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    emit('close')
    return
  }
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (['+', '=', '-', '0'].includes(event.key)) {
    event.preventDefault()
    previewZoom.setScale(
      event.key === '0' ? 1 : zoom.value + (event.key === '-' ? -0.25 : 0.25)
    )
  }
}

async function download() {
  if (downloading.value || !validUrl.value) return
  downloading.value = true
  downloadFailed.value = false
  try {
    let blob: Blob
    if (pdf.value) {
      const bytes = await pdf.value.getData()
      blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
    } else {
      const response = await fetch(props.url, {
        signal: downloadController.signal,
      })
      if (!response.ok) throw new Error('Certificate download failed')
      blob = await response.blob()
    }
    if (disposed) return
    const source = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = source
    anchor.download = certificateFilename(props.url)
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(source), 60_000)
  } catch {
    if (!disposed) downloadFailed.value = true
  } finally {
    downloading.value = false
  }
}

watch(
  () => route.fullPath,
  () => emit('close')
)
watch(() => props.url, load)

onMounted(() => {
  dialogEl.value?.showModal()
  paneEl.value?.focus({ preventScroll: true })
  observer = new ResizeObserver(() => {
    previewZoom.reset()
    measure()
  })
  if (paneEl.value) observer.observe(paneEl.value)
  measure()
  void load()
})

onBeforeUnmount(() => {
  disposed = true
  revision++
  observer?.disconnect()
  downloadController.abort()
  dialogEl.value?.close()
  void loadingTask?.destroy().catch(() => {})
})
</script>

<style scoped>
.certificate-viewer::backdrop {
  background: #f9f9fa;
}
</style>
